<template>
  <div class="min-w-0 space-y-5 px-0.5">
    <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
      <div class="min-w-0">
        <span class="panel-glass-chip inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
          {{ __('Revenue') }}
        </span>
        <p class="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          {{ __('Tehran calendar · accrued monthly recognition') }}
        </p>
      </div>

      <div class="flex min-w-0 flex-col gap-3">
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200/80 text-zinc-600 transition hover:bg-primary-500/10 hover:text-primary-600 disabled:opacity-40 dark:border-zinc-700/80 dark:text-zinc-300 dark:hover:bg-primary-500/15 dark:hover:text-primary-300"
            :disabled="loading"
            :title="__('Previous month')"
            @click="shiftMonth(-1)"
          >
            <span class="text-lg leading-none" aria-hidden="true">‹</span>
          </button>

          <div
            class="inline-flex min-w-[10.5rem] items-center justify-center rounded-full bg-primary-500 px-4 py-2 text-sm font-bold text-white shadow-sm"
          >
            {{ activeMonthLabel }}
          </div>

          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200/80 text-zinc-600 transition hover:bg-primary-500/10 hover:text-primary-600 disabled:opacity-40 dark:border-zinc-700/80 dark:text-zinc-300 dark:hover:bg-primary-500/15 dark:hover:text-primary-300"
            :disabled="loading || !canGoNextMonth"
            :title="__('Next month')"
            @click="shiftMonth(1)"
          >
            <span class="text-lg leading-none" aria-hidden="true">›</span>
          </button>

          <div class="flex overflow-hidden rounded-full border border-zinc-200/80 dark:border-zinc-700/80">
            <button
              v-for="preset in presets"
              :key="preset.id"
              type="button"
              class="px-3 py-2 text-xs font-semibold transition"
              :class="activePreset === preset.id
                ? 'bg-primary-500 text-white'
                : 'text-zinc-500 hover:bg-primary-500/10 hover:text-primary-700 dark:hover:bg-primary-500/15 dark:hover:text-primary-300'"
              :disabled="loading"
              @click="applyPreset(preset.id)"
            >
              {{ preset.label }}
            </button>
          </div>
        </div>

        <div class="flex flex-wrap items-end gap-2">
          <label class="block min-w-[9.5rem]">
            <span class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
              {{ __('From') }}
            </span>
            <date-picker
              v-model="fromJalali"
              type="date"
              :clearable="false"
              :color="pickerColor"
              :format="jalaliFormat"
              :locale="pickerLocale"
              input-class="w-full form-control form-input form-input-bordered text-sm"
              :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
              @update:model-value="activePreset = null"
            />
          </label>

          <label class="block min-w-[9.5rem]">
            <span class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
              {{ __('To') }}
            </span>
            <date-picker
              v-model="toJalali"
              type="date"
              :clearable="false"
              :color="pickerColor"
              :format="jalaliFormat"
              :locale="pickerLocale"
              input-class="w-full form-control form-input form-input-bordered text-sm"
              :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
              @update:model-value="activePreset = null"
            />
          </label>

          <button
            type="button"
            class="inline-flex h-[2.375rem] items-center justify-center rounded-full bg-primary-500 px-4 text-xs font-bold text-white shadow-sm transition hover:bg-primary-600 disabled:opacity-50"
            :disabled="loading"
            @click="applyRange"
          >
            {{ loading ? __('Refreshing…') : __('Apply') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="error" class="panel-revenue-metric text-sm text-rose-500">
      {{ error }}
    </div>

    <div class="grid min-w-0 gap-6 sm:grid-cols-2 xl:grid-cols-3">
      <button
        v-for="card in summaryCards"
        :key="card.key"
        type="button"
        class="panel-revenue-metric"
        :class="{ 'panel-revenue-metric--active': activeBucket === card.key }"
        @click="openBucket(card.key)"
      >
        <div class="panel-metric-header">
          <div class="panel-metric-header__title flex items-center gap-2">
            <h3>{{ card.label }}</h3>
            <span
              role="button"
              tabindex="0"
              class="revenue-info-trigger"
              :class="{ 'revenue-info-trigger--active': infoCard === card.key }"
              :title="__('How is this calculated?')"
              @click.stop="toggleInfo(card.key)"
              @keydown.enter.stop.prevent="toggleInfo(card.key)"
            >!</span>
          </div>
        </div>
        <p class="mb-1 text-2xl font-semibold tabular-nums tracking-tight" :class="card.tone">
          {{ formatUsd(card.value, card.negative) }}
        </p>
        <p v-if="card.hint" class="text-xs font-medium text-zinc-400 dark:text-zinc-500">
          {{ card.hint }}
        </p>
      </button>
    </div>

    <article v-if="infoCard && activeInfo" class="panel-revenue-metric">
      <div class="flex flex-wrap items-start justify-between gap-2">
        <div class="panel-metric-header mb-0">
          <div class="panel-metric-header__title">
            <h3>{{ activeInfo.label }} — {{ __('How is this calculated?') }}</h3>
          </div>
        </div>
        <button
          type="button"
          class="rounded-full px-3 py-1.5 text-xs font-semibold text-zinc-500 hover:bg-zinc-100/60 dark:hover:bg-zinc-800/60"
          @click="infoCard = null"
        >
          {{ __('Close') }}
        </button>
      </div>
      <p class="mt-2 rounded-xl bg-zinc-100/70 px-4 py-2 font-mono text-sm tabular-nums dark:bg-zinc-800/60">
        {{ activeInfo.formula }}
      </p>
      <ul class="mt-3 space-y-2 text-sm leading-6 text-zinc-600 dark:text-zinc-300">
        <li v-for="(line, i) in activeInfo.lines" :key="i" class="flex gap-2">
          <span class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
          <span>{{ line }}</span>
        </li>
      </ul>
    </article>

    <article
      v-if="(report?.monthly || []).length"
      class="panel-revenue-metric overflow-hidden !p-0"
    >
      <div class="border-b border-zinc-200/70 px-6 py-4 dark:border-zinc-800">
        <div class="panel-metric-header mb-0">
          <div class="panel-metric-header__title">
            <h3>{{ __('Month by month') }}</h3>
          </div>
        </div>
        <p class="mt-1 text-xs font-medium text-zinc-400">
          {{ __('Click a month to open its details') }}
        </p>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full min-w-[40rem] text-sm">
          <thead>
            <tr class="bg-zinc-50/80 text-left text-xs text-zinc-500 dark:bg-zinc-900/40 dark:text-zinc-400">
              <th class="px-6 py-3 font-medium">{{ __('Month') }}</th>
              <th class="px-4 py-3 font-medium">{{ __('Cash sales') }}</th>
              <th class="px-4 py-3 font-medium">{{ __('Accrued revenue') }}</th>
              <th class="px-4 py-3 font-medium">{{ __('Discounts') }}</th>
              <th class="px-4 py-3 font-medium">{{ __('Crypto fees') }}</th>
              <th class="px-4 py-3 font-medium">{{ __('Withdrawals paid') }}</th>
              <th class="px-4 py-3 font-medium">{{ __('Net profit') }}</th>
              <th class="px-6 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in report.monthly"
              :key="row.key"
              class="cursor-pointer border-t border-zinc-100 transition hover:bg-primary-50/70 dark:border-zinc-800 dark:hover:bg-primary-950/30"
              :class="focusedMonthKey === row.key ? 'bg-primary-50/90 dark:bg-primary-950/40' : ''"
              @click="openMonth(row)"
            >
              <td class="px-6 py-3.5 font-semibold text-zinc-800 dark:text-zinc-100">
                {{ row.label }}
              </td>
              <td class="px-4 py-3.5 tabular-nums">{{ formatUsd(row.cash_sales) }}</td>
              <td class="px-4 py-3.5 tabular-nums">{{ formatUsd(row.accrued_revenue) }}</td>
              <td class="px-4 py-3.5 tabular-nums text-amber-700 dark:text-amber-300">
                {{ formatUsd(row.discounts, true) }}
              </td>
              <td class="px-4 py-3.5 tabular-nums text-rose-600 dark:text-rose-400">
                {{ formatUsd(row.crypto_fees, true) }}
              </td>
              <td class="px-4 py-3.5 tabular-nums text-rose-600 dark:text-rose-400">
                {{ formatUsd(row.withdrawals_paid, true) }}
              </td>
              <td class="px-4 py-3.5 font-semibold tabular-nums" :class="netClass(row.net_profit)">
                {{ formatUsd(row.net_profit) }}
              </td>
              <td class="px-6 py-3.5 text-end">
                <span class="inline-flex rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-semibold text-white">
                  {{ __('Details') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>

    <article v-if="activeBucket && activeBucket !== 'net'" class="panel-revenue-metric overflow-hidden !p-0">
      <div class="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200/70 px-6 py-4 dark:border-zinc-800">
        <div>
          <div class="panel-metric-header mb-0">
            <div class="panel-metric-header__title">
              <h3>{{ bucketTitle }}</h3>
            </div>
          </div>
          <p class="mt-1 text-xs font-medium text-zinc-400">
            {{ rangeLabel }} · {{ linesMeta?.total ?? 0 }} {{ __('rows') }}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <a
            v-if="novaIndexUrl"
            :href="novaIndexUrl"
            class="rounded-full bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-600"
          >
            {{ __('Open in Nova') }}
          </a>
          <button
            type="button"
            class="rounded-full px-3 py-1.5 text-xs font-semibold text-zinc-500 hover:bg-zinc-100/60 dark:hover:bg-zinc-800/60"
            @click="activeBucket = null"
          >
            {{ __('Close') }}
          </button>
        </div>
      </div>

      <div v-if="linesLoading" class="px-6 py-6 text-sm text-zinc-400">{{ __('Loading…') }}</div>
      <div v-else-if="!(lines || []).length" class="px-6 py-6 text-sm text-zinc-400">
        {{ __('No rows in this range.') }}
      </div>
      <div v-else class="panel-scrollbar max-h-[28rem] overflow-auto">
        <table class="w-full min-w-[36rem] text-left text-xs">
          <thead class="sticky top-0 bg-zinc-50/95 text-zinc-500 dark:bg-zinc-900/95 dark:text-zinc-400">
            <tr>
              <th
                v-for="col in lineColumns"
                :key="col.key"
                class="px-6 py-2.5 font-medium"
              >
                {{ col.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in lines"
              :key="activeBucket + '-' + row.id"
              class="border-t border-zinc-100 dark:border-zinc-800"
            >
              <td
                v-for="col in lineColumns"
                :key="col.key"
                class="px-6 py-2.5 text-zinc-700 dark:text-zinc-200"
                :class="col.mono ? 'tabular-nums' : ''"
              >
                <a
                  v-if="col.key === 'id' && row.resource"
                  :href="resourceUrl(row.resource, row.id)"
                  class="font-semibold text-primary-600 dark:text-primary-400"
                >
                  #{{ row.id }}
                </a>
                <a
                  v-else-if="col.subscriberLink && row.chat_id"
                  :href="resourceUrl('telegram-subscribers', row.chat_id)"
                  class="font-semibold text-primary-600 dark:text-primary-400"
                >
                  {{ row[col.key] ?? '—' }}
                </a>
                <button
                  v-else-if="col.copy && row[col.key]"
                  type="button"
                  class="rounded-full bg-zinc-100/80 px-2.5 py-1 font-mono text-[11px] text-zinc-600 transition hover:bg-primary-500/10 hover:text-primary-600 dark:bg-zinc-800/80 dark:text-zinc-300 dark:hover:bg-primary-500/15 dark:hover:text-primary-300"
                  :title="__('Copy UID')"
                  @click="onCopyUid(row[col.key])"
                >
                  {{ row[col.key] }}
                </button>
                <span v-else-if="col.money">{{ formatUsd(row[col.key], col.negative) }}</span>
                <span v-else-if="col.date">{{ formatWhen(row[col.key]) }}</span>
                <span v-else>{{ row[col.key] ?? '—' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="(linesMeta?.last_page || 1) > 1"
        class="flex items-center justify-between border-t border-zinc-200/70 px-6 py-3 dark:border-zinc-800"
      >
        <button
          type="button"
          class="rounded-full px-3 py-1.5 text-xs font-semibold text-primary-600 disabled:opacity-40 dark:text-primary-400"
          :disabled="linesPage <= 1 || linesLoading"
          @click="loadLines(linesPage - 1)"
        >
          {{ __('Previous') }}
        </button>
        <span class="text-xs text-zinc-400">
          {{ linesPage }} / {{ linesMeta.last_page }}
        </span>
        <button
          type="button"
          class="rounded-full px-3 py-1.5 text-xs font-semibold text-primary-600 disabled:opacity-40 dark:text-primary-400"
          :disabled="linesPage >= linesMeta.last_page || linesLoading"
          @click="loadLines(linesPage + 1)"
        >
          {{ __('Next') }}
        </button>
      </div>
    </article>
  </div>
</template>

<script>
import Vue3PersianDatetimePicker from 'vue3-persian-datetime-picker'
import jMoment from 'moment-jalaali'
import CopiesToClipboard from '../../mixins/CopiesToClipboard'

export default {
  mixins: [CopiesToClipboard],

  components: {
    DatePicker: Vue3PersianDatetimePicker,
  },

  data() {
    const start = jMoment().startOf('jMonth')
    const end = jMoment().endOf('jMonth')

    return {
      jalaliFormat: 'jYYYY/jMM/jDD',
      pickerLocale: 'fa,en',
      pickerColor: 'rgb(59, 130, 246)',
      fromJalali: start.format('jYYYY/jMM/jDD'),
      toJalali: end.format('jYYYY/jMM/jDD'),
      from: start.format('YYYY-MM-DD'),
      to: end.format('YYYY-MM-DD'),
      loading: false,
      error: '',
      report: null,
      activeBucket: null,
      infoCard: null,
      activePreset: 'this_month',
      lines: [],
      linesMeta: null,
      linesPage: 1,
      linesLoading: false,
    }
  },

  computed: {
    presets() {
      return [
        { id: 'this_month', label: this.__('This month') },
        { id: 'last_month', label: this.__('Last month') },
        { id: 'last_3_months', label: this.__('Last 3 months') },
        { id: 'this_year', label: this.__('This year') },
      ]
    },

    activeMonthLabel() {
      const m = jMoment(this.from, 'YYYY-MM-DD')
      if (!m.isValid()) return '—'
      return m.format('jMMMM jYYYY')
    },

    canGoNextMonth() {
      const cursor = jMoment(this.from, 'YYYY-MM-DD').startOf('jMonth')
      const current = jMoment().startOf('jMonth')
      return cursor.isBefore(current)
    },

    rangeLabel() {
      if (!this.from || !this.to) return ''
      const a = jMoment(this.from, 'YYYY-MM-DD').format(this.jalaliFormat)
      const b = jMoment(this.to, 'YYYY-MM-DD').format(this.jalaliFormat)
      return `${a} → ${b}`
    },

    focusedMonthKey() {
      if (!this.from || !this.to) return null
      const start = jMoment(this.from, 'YYYY-MM-DD')
      const end = jMoment(this.to, 'YYYY-MM-DD')
      if (!start.isValid() || !end.isValid()) return null
      if (
        start.format('jYYYY-jMM') === end.format('jYYYY-jMM') &&
        start.format('jDD') === '01' &&
        end.format('jDD') === end.clone().endOf('jMonth').format('jDD')
      ) {
        return start.format('jYYYY-jMM')
      }
      return null
    },

    summaryCards() {
      const r = this.report || {}
      const counts = r.counts || {}

      return [
        {
          key: 'cash',
          label: this.__('Cash sales'),
          value: r.cash_sales,
          hint: this.__(':n activations', { n: counts.cash ?? 0 }),
          tone: 'text-emerald-600 dark:text-emerald-400',
        },
        {
          key: 'accrued',
          label: this.__('Accrued revenue'),
          value: r.accrued_revenue,
          hint: this.__(':n overlapping subscriptions', { n: counts.accrued ?? 0 }),
          tone: 'text-primary-600 dark:text-primary-400',
        },
        {
          key: 'discounts',
          label: this.__('Discounts'),
          value: r.discounts,
          negative: true,
          hint: this.__(':n discounted', { n: counts.discounts ?? 0 }),
          tone: 'text-amber-700 dark:text-amber-300',
        },
        {
          key: 'fees',
          label: this.__('Crypto fees'),
          value: r.crypto_fees,
          negative: true,
          hint:
            (r.crypto_fee_percent || 0) > 0
              ? this.__('Fallback :p%', { p: r.crypto_fee_percent })
              : this.__(':n deposits', { n: counts.fees ?? 0 }),
          tone: 'text-rose-600 dark:text-rose-400',
        },
        {
          key: 'withdrawals',
          label: this.__('Withdrawals paid'),
          value: r.withdrawals_paid,
          negative: true,
          hint: this.__(':n payouts', { n: counts.withdrawals ?? 0 }),
          tone: 'text-rose-600 dark:text-rose-400',
        },
        {
          key: 'net',
          label: this.__('Net profit'),
          value: r.net_profit,
          hint: this.__('Accrued − fees − withdrawals'),
          tone: this.netClass(r.net_profit),
        },
      ]
    },

    /**
     * Full explanation for the card whose "!" is open — the formula and
     * every rule behind the number, so nobody has to guess what "net
     * profit" or "accrued" means.
     */
    activeInfo() {
      const r = this.report || {}
      const card = this.summaryCards.find((c) => c.key === this.infoCard)
      if (!card) return null

      const usd = (v) => this.formatUsd(v)
      const feePercent = r.crypto_fee_percent || 0

      const texts = {
        cash: {
          formula: this.__('Cash sales = Σ amount paid (activations inside the range)'),
          lines: [
            this.__('Every paid subscription whose activation date falls inside the selected range, at the full amount the user paid (after any discount).'),
            this.__('Counted on the day it was activated — the whole amount lands in that month even when the subscription runs into the next one.'),
            this.__('Free / zero-amount subscriptions are not included.'),
          ],
        },
        accrued: {
          formula: this.__('Accrued revenue = Σ amount paid × (days inside the range ÷ subscription length)'),
          lines: [
            this.__('Each paid subscription is spread evenly over its own period (start → end), day by day, and only the part that overlaps the selected range is recognised here.'),
            this.__('So a 7-day subscription sold on the last day of a month puts about one day of its price in that month and the rest in the next one.'),
            this.__('This is the accounting view of revenue (accrual); "Cash sales" is the money-received view. Over the whole life of a subscription both add up to the same amount.'),
          ],
        },
        discounts: {
          formula: this.__('Discounts = Σ list price × discount %'),
          lines: [
            this.__('For activations inside the range that carried a discount, the difference between the plan\'s list price and what the user paid.'),
            this.__('Informational only: cash sales and accrued revenue already use the discounted amount, so this is not subtracted again anywhere.'),
          ],
        },
        fees: {
          formula: this.__('Crypto fees = Σ deposit fee (or amount × fallback % when the gateway sent no fee)'),
          lines: [
            this.__('The network / gateway fee taken off every crypto deposit credited inside the range.'),
            feePercent > 0
              ? this.__('When a deposit has no fee recorded, :p% of its amount is assumed.', { p: feePercent })
              : this.__('Only fees the gateway actually reported are counted (no fallback percent is configured).'),
            this.__('Fees are counted in full on the day of the deposit, not spread over the subscription.'),
          ],
        },
        withdrawals: {
          formula: this.__('Withdrawals paid = Σ referral-wallet payouts marked paid inside the range'),
          lines: [
            this.__('Money actually sent out to users from their referral wallet — only withdrawals with status "paid" and a paid-at date inside the range.'),
            this.__('Pending or rejected withdrawals are not included.'),
          ],
        },
        net: {
          formula: this.__('Net profit = Accrued revenue − Crypto fees − Withdrawals paid'),
          lines: [
            this.__('For this range: :a − :f − :w = :n', {
              a: usd(r.accrued_revenue),
              f: usd(r.crypto_fees),
              w: usd(r.withdrawals_paid),
              n: usd(r.net_profit),
            }),
            this.__('It uses accrued (day-by-day) revenue, not cash sales. Fees and payouts are taken in full on the day they happen.'),
            this.__('That is why a month that sells subscriptions near its end can show a small or negative net profit: the fee is charged now, most of the revenue is recognised next month.'),
            this.__('Discounts are not subtracted here — the amounts above are already after discount.'),
          ],
        },
      }

      const info = texts[card.key]
      return info ? { label: card.label, ...info } : null
    },

    bucketTitle() {
      const hit = this.summaryCards.find(c => c.key === this.activeBucket)
      return hit?.label || ''
    },

    lineColumns() {
      const cols = {
        cash: [
          { key: 'id', label: this.__('ID') },
          { key: 'subscriber', label: this.__('Subscriber'), subscriberLink: true },
          { key: 'exchange_uid', label: this.__('Exchange UID'), copy: true },
          { key: 'plan', label: this.__('Plan') },
          { key: 'amount_paid_usd', label: this.__('Amount Paid'), money: true, mono: true },
          { key: 'activated_at', label: this.__('Activated At'), date: true },
        ],
        accrued: [
          { key: 'id', label: this.__('ID') },
          { key: 'subscriber', label: this.__('Subscriber') },
          { key: 'plan', label: this.__('Plan') },
          { key: 'amount_paid_usd', label: this.__('Amount Paid'), money: true, mono: true },
          { key: 'accrued_usd', label: this.__('Accrued'), money: true, mono: true },
          { key: 'starts_at', label: this.__('Starts At'), date: true },
          { key: 'ends_at', label: this.__('Ends At'), date: true },
        ],
        discounts: [
          { key: 'id', label: this.__('ID') },
          { key: 'subscriber', label: this.__('Subscriber') },
          { key: 'plan', label: this.__('Plan') },
          { key: 'list_price_usd', label: this.__('List Price'), money: true, mono: true },
          { key: 'discount_percent', label: this.__('Discount %'), mono: true },
          { key: 'discount_usd', label: this.__('Discount'), money: true, negative: true, mono: true },
          { key: 'activated_at', label: this.__('Activated At'), date: true },
        ],
        fees: [
          { key: 'id', label: this.__('ID') },
          { key: 'chat_id', label: this.__('Chat ID') },
          { key: 'amount', label: this.__('Amount (USD)'), money: true, mono: true },
          { key: 'fee_effective', label: this.__('Fee'), money: true, negative: true, mono: true },
          { key: 'network', label: this.__('Network') },
          { key: 'credited_at', label: this.__('Credited at'), date: true },
        ],
        withdrawals: [
          { key: 'id', label: this.__('ID') },
          { key: 'subscriber', label: this.__('Subscriber') },
          { key: 'amount_usd', label: this.__('Amount (USD)'), money: true, negative: true, mono: true },
          { key: 'exchange_uid', label: this.__('Exchange UID') },
          { key: 'tracking_code', label: this.__('Tracking code') },
          { key: 'paid_at', label: this.__('Paid at'), date: true },
        ],
      }

      return cols[this.activeBucket] || []
    },

    novaIndexUrl() {
      const map = {
        cash: 'subscriptions',
        accrued: 'subscriptions',
        discounts: 'subscriptions',
        fees: 'payment-deposits',
        withdrawals: 'wallet-withdrawals',
      }
      const resource = map[this.activeBucket]
      return resource ? this.resourceUrl(resource) : null
    },
  },

  mounted() {
    this.fetchSummary()
  },

  methods: {
    onCopyUid(label) {
      // label is "Exchange - UID"; only the UID itself gets copied.
      const uid = String(label).split(' - ').pop().trim()
      this.copyValueToClipboard(uid)
      if (typeof Nova !== 'undefined' && typeof Nova.success === 'function') {
        Nova.success(this.__('UID copied.'))
      }
    },

    resourceUrl(resource, id = null) {
      const path = id != null
        ? `/resources/${resource}/${id}`
        : `/resources/${resource}`
      return typeof Nova !== 'undefined' && Nova.url
        ? Nova.url(path)
        : `/admin${path}`
    },

    toGregorian(jalali) {
      if (!jalali) return null
      const m = jMoment(jalali, this.jalaliFormat)
      return m.isValid() ? m.format('YYYY-MM-DD') : null
    },

    setJalaliRange(start, end) {
      this.fromJalali = start.format(this.jalaliFormat)
      this.toJalali = end.format(this.jalaliFormat)
      this.from = start.format('YYYY-MM-DD')
      this.to = end.format('YYYY-MM-DD')
    },

    applyPreset(id) {
      const now = jMoment()
      let start
      let end

      if (id === 'this_month') {
        start = now.clone().startOf('jMonth')
        end = now.clone().endOf('jMonth')
      } else if (id === 'last_month') {
        start = now.clone().subtract(1, 'jMonth').startOf('jMonth')
        end = now.clone().subtract(1, 'jMonth').endOf('jMonth')
      } else if (id === 'last_3_months') {
        start = now.clone().subtract(2, 'jMonth').startOf('jMonth')
        end = now.clone().endOf('jMonth')
      } else if (id === 'this_year') {
        start = now.clone().startOf('jYear')
        end = now.clone().endOf('jMonth')
      } else {
        return
      }

      this.activePreset = id
      this.setJalaliRange(start, end)
      this.activeBucket = null
      this.fetchSummary()
    },

    shiftMonth(delta) {
      const base = jMoment(this.from, 'YYYY-MM-DD')
      const cursor = (base.isValid() ? base : jMoment()).startOf('jMonth').add(delta, 'jMonth')
      const current = jMoment().startOf('jMonth')
      if (delta > 0 && cursor.isAfter(current)) return

      this.activePreset = null
      this.setJalaliRange(cursor.clone().startOf('jMonth'), cursor.clone().endOf('jMonth'))
      this.activeBucket = null
      this.fetchSummary()
    },

    openMonth(row) {
      if (!row?.from || !row?.to) return
      const start = jMoment(row.from, 'YYYY-MM-DD')
      const end = jMoment(row.to, 'YYYY-MM-DD')
      if (!start.isValid() || !end.isValid()) return

      this.activePreset = null
      this.setJalaliRange(start.startOf('day'), end.endOf('day'))
      this.activeBucket = null
      this.fetchSummary().then(() => {
        this.openBucket('cash')
      })
    },

    applyRange() {
      const from = this.toGregorian(this.fromJalali)
      const to = this.toGregorian(this.toJalali)
      if (!from || !to) {
        this.error = this.__('Choose a valid date range.')
        return
      }
      this.from = from
      this.to = to
      this.activePreset = null
      this.activeBucket = null
      this.fetchSummary()
    },

    async fetchSummary() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await Nova.request().get('/nova-api/billing/revenue/summary', {
          params: { from: this.from, to: this.to },
        })
        this.report = data
        if (data.from) {
          this.from = data.from
          this.fromJalali = jMoment(data.from, 'YYYY-MM-DD').format(this.jalaliFormat)
        }
        if (data.to) {
          this.to = data.to
          this.toJalali = jMoment(data.to, 'YYYY-MM-DD').format(this.jalaliFormat)
        }
      } catch (e) {
        this.error = e?.response?.data?.message || this.__('Failed to load revenue report.')
      } finally {
        this.loading = false
      }
    },

    toggleInfo(key) {
      this.infoCard = this.infoCard === key ? null : key
    },

    openBucket(key) {
      if (key === 'net') {
        this.activeBucket = 'net'
        return
      }
      if (this.activeBucket === key) {
        this.activeBucket = null
        return
      }
      this.activeBucket = key
      this.loadLines(1)
    },

    async loadLines(page = 1) {
      if (!this.activeBucket || this.activeBucket === 'net') return
      this.linesLoading = true
      this.linesPage = page
      try {
        const { data } = await Nova.request().get('/nova-api/billing/revenue/lines', {
          params: {
            bucket: this.activeBucket,
            from: this.from,
            to: this.to,
            page,
            per_page: 25,
          },
        })
        this.lines = data.data || []
        this.linesMeta = data.meta || null
      } catch (e) {
        this.lines = []
        this.linesMeta = null
        this.error = e?.response?.data?.message || this.__('Failed to load lines.')
      } finally {
        this.linesLoading = false
      }
    },

    formatUsd(value, asNegative = false) {
      const n = Number(value || 0)
      const abs = Math.abs(n).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      if (asNegative || n < 0) {
        return `−$${abs}`
      }
      return `$${abs}`
    },

    formatWhen(iso) {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('fa-IR', {
          timeZone: 'Asia/Tehran',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      } catch {
        return iso
      }
    },

    netClass(value) {
      const n = Number(value || 0)
      if (n > 0) return 'text-emerald-600 dark:text-emerald-400'
      if (n < 0) return 'text-rose-600 dark:text-rose-400'
      return 'text-zinc-700 dark:text-zinc-200'
    },
  },
}
</script>
